/* Signed in by the app.
 *
 * The app opens a page of the web app inside itself — the live studio — and
 * hands over who is signed in with a one-minute, one-time code, rather than
 * asking them to sign in a second time. The code is exchanged for a session
 * of its own, and the page goes on to where the app was sending them.
 */
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { api, tokens } from '@/api/client'
import { markEmbedded } from '@/lib/embedded'
import type { Session } from '@/types/api'

/** Only a path inside this app — never somewhere else by way of `next`. */
function safeNext(next: string | null): string {
  if (!next || !next.startsWith('/') || next.startsWith('//')) return ''
  return next.slice(1)
}

export function HandoffPage() {
  const [params] = useSearchParams()
  const [failed, setFailed] = useState<string | null>(null)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const code = params.get('code')
    if (params.get('embed') === '1') markEmbedded()
    if (!code) {
      setFailed('This link is missing its code. Open it again from the app.')
      return
    }
    api
      .post<Session>('/auth/handoff/redeem', { code }, { auth: false })
      .then((session) => {
        tokens.save(session)
        // A full load, as after signing in: every cache starts clean for
        // the person now signed in. `replace`, so Back does not return to a
        // spent code.
        window.location.replace(import.meta.env.BASE_URL + safeNext(params.get('next')))
      })
      .catch(() => {
        setFailed('That link has expired. Go back and open it again from the app.')
      })
  }, [params])

  return (
    <div className="grid min-h-screen grid-cols-[minmax(0,1fr)] place-items-center bg-canvas p-s5">
      {failed ? (
        <p className="max-w-[360px] text-center text-md font-semibold text-ink-2">{failed}</p>
      ) : (
        <Loader2 className="h-6 w-6 animate-spin text-ink-3" aria-label="Signing you in" />
      )}
    </div>
  )
}
